import Color from "@faire/web--source/slate/Color";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import * as React from "react";
import styled, { css } from "styled-components";

export type IValueProp = {
  title: React.ReactNode;
  description: React.ReactNode;
  ctaLink?: React.ReactNode;
};

interface IProps {
  valueProps: IValueProp[];
  textColor?: string;
  className?: string;
}

const AnimatedValuePropsBase: React.FC<IProps> = ({
  valueProps,
  textColor,
  className,
}) => {
  const [playingAnimation, setPlayingAnimation] = useState<boolean>(false);
  const ref = useDoActionWhenVisible(() => setPlayingAnimation(true));
  return (
    <ValuePropsContainer ref={ref} className={className}>
      {valueProps.map(({ title, description, ctaLink }, index) => (
        <ValueProp key={index} $index={index} $play={playingAnimation}>
          <Typography
            variant={{
              mobileAndAbove: "paragraphSansMedium",
              desktopAndAbove: "subheadingSansMedium",
            }}
            color={textColor as Color}
          >
            {title}
          </Typography>
          <Spacer size={{ mobileAndAbove: "1x", desktopAndAbove: "2x" }} />
          <Typography variant="paragraphSansRegular" color={textColor as Color}>
            {description}
          </Typography>
          {ctaLink ? (
            <>
              <Spacer size="1x" />
              <Row align="center">
                <Typography
                  variant="paragraphSansRegular"
                  color={textColor as Color}
                >
                  {ctaLink}
                </Typography>
                <Spacer size={4} />
                <Arrow direction="right" />
              </Row>
            </>
          ) : null}
          <ResponsiveContentV2
            tabletAndBelow={
              index < valueProps.length - 1 ? <Spacer size="5x" /> : undefined
            }
          />
        </ValueProp>
      ))}
    </ValuePropsContainer>
  );
};

export const AnimatedValueProps = observer(AnimatedValuePropsBase);

const FADE_IN_ANIMATION_TIME = 0.4;
const OVERLAP_OFFSET = 0.4;
/**
 * @Todd
 * We want to multiply the index * FADE_IN_ANIMATION_TIME by a number below 1
 * to cause subsequent animations to play before the previous one ends.
 * This will make the animation feel smoother and not as choppy
 */
const ValueProp = styled(Column)<{ $index: number; $play?: boolean }>`
  min-width: 280px;
  animation: fadeInDown ${FADE_IN_ANIMATION_TIME}s ease-out forwards;
  opacity: 0;

  ${({ $index: index }) => css`
    ${responsiveMediaQueries.desktopAndAbove} {
      margin-left: ${index > 0 ? getSpacing("10x") : 0};
      animation-delay: ${index * FADE_IN_ANIMATION_TIME * OVERLAP_OFFSET}s;
    }
  `};
  ${({ $play: play }) => css`
    animation-play-state: ${play ? "running" : "paused"};
  `};
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-30%);
    }
    to {
      opacity: 1;
      transform: unset;
    }
  }
`;

const ValuePropsContainer = styled(Flex).attrs({
  justify: "space-between",
  align: "flex-start",
  direction: {
    mobileAndAbove: "column",
    desktopAndAbove: "row",
  },
})`
  width: 100%;
  ${responsiveMediaQueries.tablet} {
    padding: 0 114px;
  }
`;
